import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  Input,
  Form,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import Select from "react-select";
import Spinners from "components/Common/Spinner";

import {
  getAllVenue,
  createVenue,
  updateVenue,
  deleteVenue,
  getAllCountries,
} from "api";
import useCountryStore from "../../../store/countryStore";
import { useAutoSelectCountry } from "../../../hooks/useAutoSelectCountry";
import { getUserRole, getCountryId } from "utils/storage";
import ImageCropper from "../../../components/Common/ImageCropper";

const VenueList = () => {
  const role = getUserRole();

  document.title = "Venue List | EventoX";

  const [venues, setVenues] = useState([]);
  const [countries, setCountries] = useState([]);
  const [venueId, setVenueId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [selectedCropFile, setSelectedCropFile] = useState(null);
  const editData = venues.find((v) => v.userId === venueId) || {};
  const STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const handleImageChange = (event) => {
    let file = event.target.files[0];
    if (file) {
      setSelectedCropFile(file);
      setIsCropperOpen(true);
    }
    event.target.value = '';
  };
  
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editData?.name || "",
      email: editData?.email || "",
      phoneNo: editData?.phoneNo || "",
      location: editData?.location?.toString() || (role === 5 ? getCountryId() : ""),
      status: editData?.status || "active",
      profileImage: editData?.profileImage || "",
      address: editData?.address || "",
      link: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Name"),
      phoneNo: Yup.string().required("Please Enter Phone"),
      email: Yup.string()
        .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "Please Enter Valid Email")
        .required("Please Enter Email"),
      location: Yup.string().required("Country is required"),
      address: Yup.string().optional(),
    }),
    onSubmit: async values => {

      setLoading(true);

      let data = new FormData();
      data.append("name", values["name"]);
      data.append("phoneNo", values["phoneNo"]);
      data.append("email", values["email"]);
      data.append("location", role === 5 ? getCountryId() : values["location"]);
      data.append("profileImage", values["profileImage"]);
      data.append("address", values["address"]);
      data.append("url", values["url"]);
        try {

        if (isEdit) {
          data.append("status", values.status);
          await updateVenue(venueId, data);
          toast.success("Venue Updated Successfully");
        } else {
          data.append("username", values["email"]);
          data.append("password", "password123");
          data.append("userRole", 3);
          await createVenue(data);
          toast.success("Venue Created Successfully");
        }

      } catch (error) {
        toast.error("Error saving venue");
      } finally {
        setLoading(false);
        getVenues();
        toggle();
      }
    },
  });

  const { selectedCountryId } = useCountryStore();

  useAutoSelectCountry({
    countryList: countries,
    setFieldValue: validation.setFieldValue,
    fieldName: "location"
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await getVenues();
        const countryResp = await getAllCountries();
        setCountries(countryResp);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCountryId]);

  const getVenues = async () => {
    try {
      let res = await getAllVenue();
      const role = getUserRole();
      const fixedCountryId = getCountryId();
      const currentCountryId = (role === 1 || role === '1') ? selectedCountryId : fixedCountryId;

      let filtered = (res || []).filter((u) => {
        const isVenue = Number(u.userRole) === 3;
        const matchesCountry = !currentCountryId || u.location?.toString() === currentCountryId.toString();
        return isVenue && matchesCountry;
      });
      setVenues(filtered);
    } catch (error) {
      console.log(error);
    }
  };

  const toggle = () => {
    setModal(!modal);
  };

  const handleAddClick = () => {
    setVenueId(null);
    setIsEdit(false);
    setSelectedImage("");
    validation.resetForm();
    toggle();
  };

  const handleVenueClick = (v) => {
    setVenueId(v.userId);
    setIsEdit(true);

    validation.setValues({
      name: v.name,
      email: v.email,
      phoneNo: v.phoneNo,
      location: v.location?.toString() || "",
      status: (v.status ||"active").toLowerCase(),
      profileImage: v.profileImage || "",
      address: v.address || "",
    });

    toggle();
  };

  const onClickDelete = (v) => {
    setVenueId(v.userId);
    setDeleteModal(true);
  };

  const handleDeleteVenue = async () => {
    try {
      setLoading(true);
      await deleteVenue(venueId);
      toast.success("Venue Deleted Successfully");
      getVenues();
    } catch (error) {
      toast.error("Error Deleting Venue");
    } finally {
      setLoading(false);
      setDeleteModal(false);
    }
  };
  // const handleDemoImagesChange = (e) => {
  //   const files = Array.from(e.target.files);
  
  //   validation.setFieldValue("demoImages", files);
  
  //   const previews = files.map(file => URL.createObjectURL(file));
  //   setDemoImagePreviews(previews);
  // };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "profileImage",
        cell: (cell) => {
          const img = cell.getValue();
          const name = cell.row.original.name;
      
          return (
            <>
              {!img ? (
                <div className="avatar-xs">
                  <span className="avatar-title rounded-circle">
                    {name?.charAt(0)}
                  </span>
                </div>
              ) : (
                <img
                  src={img}
                  alt="profile"
                  className="rounded-circle avatar-xs"
                  style={{ objectFit: "cover", width: "32px", height: "32px" }}
                />
              )}
            </>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Email",
        accessorKey: "email",
      },
      {
        header: "Phone",
        accessorKey: "phoneNo",
      },
      {
        header: "Country",
        accessorFn: (row) => {
          const countryData = countries.find(c => String(c.countryId) === String(row.location));
          return countryData ? countryData.countryName : "";
        },
        id: "country",
        cell: (cell) => cell.getValue(),
      },
      {
        header: "Status",
        accessorKey: "status",
        filterFn: (row, columnId, filterValue) => {
          const val = row.getValue(columnId);
          if (val === undefined || val === null) return false;
          return String(val).toLowerCase().includes(String(filterValue).toLowerCase());
        },
      },
      {
        header: "Action",
        cell: (cellProps) => {
          const row = cellProps.row.original;

          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18"></i>
              </DropdownToggle>

              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem onClick={() => handleVenueClick(row)}>
                  <i
                    className="mdi mdi-pencil font-size-16 text-success me-1"
                    id={`edit-${row.userId}`}
                  ></i>
                  Edit
                  <UncontrolledTooltip placement="top" target={`edit-${row.userId}`}>
                    Edit
                  </UncontrolledTooltip>
                </DropdownItem>

                <DropdownItem onClick={() => onClickDelete(row)}>
                  <i
                    className="mdi mdi-trash-can font-size-16 text-danger me-1"
                    id={`delete-${row.userId}`}
                  ></i>
                  Delete
                  <UncontrolledTooltip placement="top" target={`delete-${row.userId}`}>
                    Delete
                  </UncontrolledTooltip>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        }
      },
    ],
    [countries]
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteVenue}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>

          <Breadcrumbs title="Dashboard" breadcrumbItem="Venue List" />

          <Row>
            {
              isLoading ? (
                <Spinners setLoading={setLoading} />
              ) : (
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <TableContainer
                        columns={columns}
                        data={venues || []}
                        isGlobalFilter={true}
                        isPagination={true}
                        SearchPlaceholder="Search..."
                        isCustomPageSize={true}
                        isAddButton={true}
                        handleUserClick={handleAddClick}
                        buttonClass="btn btn-success btn-rounded waves-effect waves-light addContact-modal mb-2"
                        buttonName="New Venue"
                        tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                        theadClass="table-light"
                        paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                        pagination="pagination"
                      />
                    </CardBody>
                  </Card>
                </Col>
              )
            }

            <Modal isOpen={modal} toggle={toggle}>
              <ModalHeader toggle={toggle} tag="h4">
                {!!isEdit ? "Edit Venue" : "Add Venue"}
              </ModalHeader>

              <ModalBody>
                <Form
                  onSubmit={e => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <Row>
                    <Col xs={12}>

                      <div className="mb-3">
                        <Label className="form-label">Name</Label>
                        <Input
                          name="name"
                          type="text"
                          placeholder="Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name || ""}
                          invalid={
                            validation.touched.name &&
                            validation.errors.name
                              ? true
                              : false
                          }
                        />
                        {validation.touched.name &&
                          validation.errors.name && (
                            <FormFeedback type="invalid">
                              {validation.errors.name}
                            </FormFeedback>
                          )}
                      </div>
                      <div className="mb-3">
                      <Label className="form-label">Address</Label>
                      <Input
                        name="address"
                        type="textarea"
                        rows="3"
                        placeholder="Full venue address"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.address || ""}
                      />
                    </div>
                      <div className="mb-3">
                        <Label className="form-label">Email</Label>
                        <Input
                          name="email"
                          type="email"
                          placeholder="Email"
                          onChange={validation.handleChange}
                          readOnly={isEdit}
                          className={isEdit ? "bg-light" : ""}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email &&
                            validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email &&
                          validation.errors.email && (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Phone No</Label>
                        <Input
                          name="phoneNo"
                          type="text"
                          placeholder="Phone no"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.phoneNo || ""}
                          invalid={
                            validation.touched.phoneNo &&
                            validation.errors.phoneNo
                              ? true
                              : false
                          }
                        />
                        {validation.touched.phoneNo &&
                          validation.errors.phoneNo && (
                            <FormFeedback type="invalid">
                              {validation.errors.phoneNo}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Country</Label>
                        {role === 5 ? (
                        <Input
                          type="text"
                          value={
                            countries?.find(c => String(c.countryId) === String(getCountryId()))?.countryName || ""
                          }
                          disabled
                            className="form-control"
                          />
                        ) : (
                        <Select
                          name="location"
                          options={countries?.map(c => ({
                            value: c.countryId.toString(),
                            label: c.countryName,
                          }))}
                          value={
                            countries
                              ?.map(c => ({
                                value: c.countryId.toString(),
                                label: c.countryName,
                              }))
                              .find(option =>
                                option.value ===
                                validation.values.location.toString()
                              ) || null
                          }
                          onChange={(option) => {
                            validation.setFieldValue(
                              "location",
                              option ? option.value : ""
                            );
                          }}
                          placeholder="Select Country"
                          menuContainerStyle={{'zIndex': 999}}
                                          styles={{
                                            menu: provided => ({ ...provided, zIndex: 9999 })
                                        }}
                        />
                        )}
                      </div>
                      <div className="mb-3">
                    <Label className="form-label">
                       Link <span className="text-muted">(not saved)</span>
                    </Label>
                    <Input
                      name="demoLink"
                      type="url"
                      placeholder="https://example.com"
                      onChange={validation.handleChange}
                      value={validation.values.demoLink || ""}
                    />
                  </div>
                      <div className="mb-3 ">
                      <Label className="form-label">Profile Image</Label>
                      </div>
                      <div>
                      <div className="position-relative d-inline-block">

                        {/* Upload Button */}
                        <Label
                          htmlFor="venue-profile-image"
                          className="position-absolute bottom-0 end-0 mb-0 cursor-pointer"
                          id="venueProfileImgBtn"
                        >
                          <div className="avatar-xs">
                            <div className="avatar-title bg-light border rounded-circle shadow text-muted font-size-16">
                              <i className="bx bxs-camera"></i>
                            </div>
                          </div>
                        </Label>

                        <input
                          className="d-none"
                          id="venue-profile-image"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                        />

                        {/* Image Preview */}
                        <div className="avatar-lg">
                          <img
                            src={
                              selectedImage ||
                              editData?.profileImage ||
                              "/placeholder-user.png"
                            }
                            className="rounded-circle avatar-lg"
                            alt="profile"
                            style={{ objectFit: "cover", width: "100px", height: "100px" }}
                          />
                        </div>
                      </div>

                      {validation.touched.profileImage && validation.errors.profileImage && (
                        <FormFeedback type="invalid" className="d-block">
                          {validation.errors.profileImage}
                        </FormFeedback>
                      )}
                    </div>
                    <div className="mb-3 pt-3">
                      {isEdit && (
                        <div className="mb-3">
                          <Label className="form-label">Status</Label>
                          <Select
                          options={STATUS_OPTIONS}
                          value={STATUS_OPTIONS.find(
                            opt => opt.value === validation.values.status
                          )}
                          onChange={(option) =>
                            validation.setFieldValue("status", option.value)
                          }
                          menuContainerStyle={{ zIndex: 999 }}
                          styles={{
                            menu: provided => ({ ...provided, zIndex: 9999 })
                          }}
                        />
                        </div>
                      )}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <div className="text-end">
                        <button
                          type="submit"
                          className="btn btn-success save-user"
                        >
                          {isLoading ? "Loading...." : "Save"}
                        </button>
                      </div>
                    </Col>
                  </Row>

                </Form>
              </ModalBody>
            </Modal>

          </Row>
        </Container>
      </div>

      <Modal isOpen={isCropperOpen} toggle={() => setIsCropperOpen(false)} size="lg" style={{ zIndex: 1060 }}>
        <ModalHeader toggle={() => setIsCropperOpen(false)}>Crop Image</ModalHeader>
        <ModalBody>
          {selectedCropFile && (
            <ImageCropper
              imageFile={selectedCropFile}
              onCropComplete={(croppedFile) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  setSelectedImage(reader.result);
                  validation.setFieldValue("profileImage", croppedFile);
                };
                reader.readAsDataURL(croppedFile);
                setIsCropperOpen(false);
              }}
            />
          )}
        </ModalBody>
      </Modal>

    </React.Fragment>
  );
};

export default withRouter(VenueList);
